import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveal — lightweight scroll-triggered reveal wrapper for public pages.
 * Purely visual: fades + slides children in when they enter the viewport.
 * Falls back to fully visible content if IntersectionObserver is unavailable.
 *
 * Props:
 *  - delay (number, ms): transition delay for staggered reveals
 *  - className (string): extra classes on the wrapper
 *  - as (string): element tag to render (default 'div')
 */
const Reveal = ({ children, delay = 0, className = '', as: Tag = 'div' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
